import React, { useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import NavigationBar from "@components/NavigationBar";
import Footer from "@components/Footer";
import { Route, Routes } from "react-router-dom";
import NotFoundPage from "@app/errors/NotFoundPage";
import WalletModal from "@components/modals/WalletModal";
import { ModalContextProvider } from "@contexts/ModalContext";
import HomePage from "@app/home/HomePage";
import CollectionPage from "@app/collection/CollectionPage";
import CollectionsPage from "@app/collection/CollectionsPage";
import CreateCollectionPage from "@app/collection/create/CreateCollectionPage";
import EditCollectionPage from "@app/collection/edit/EditCollectionPage";
import ProfilePage from "@app/profile/ProfilePage";
import EditProfilePage from "@app/profile/edit/EditProfilePage";
import CreateItemPage from "@app/item/create/CreateItemPage";
import { MintableItemPage } from "@app/mintable/MintableItemPage";
import { AlertDialogProvider } from "@contexts/AlertDialogContext";
import { ChainSelector } from "@app/examples/ChainSelector";
import { CoinsContextProvider } from "@contexts/CoinsContext";
import { TxModalProvider } from "@contexts/TxModalContext";
import { RegisterModal } from "@components/modals/RegisterModal";
import { AlertDialog } from "@components/alert-dialog/AlertDialog";
import { AccountContextProvider } from "@contexts/AccountContext";
import { TxModal } from "@components/tx-modal/TxModal";
import { TokensPage } from "@app/tokens/TokensPage";
import { ItemPage } from "@app/item/ItemPage";
import AdminPage from "@app/admin/AdminPage";
import { NftPage } from "@app/nft/NFTPage";
import BulkCreateItemPage from "@app/item/create/BulkCreateItemPage";
import UserPage from "@app/user/UserPage";
import EditItemPage from "@app/item/edit/EditItemPage";
import AirForceLunc from "@app/air-force-lunc/AirForceLunc";
import LuncZombies from "@app/lunc-zombies/LuncZombies";
import LaunchpadPage from "@app/launchpad/LaunchpadPage";
import {
  SITE_DESCRIPTION,
  SITE_NAME,
  SITE_OG_IMAGE,
  SITE_OG_IMAGE_HEIGHT,
  SITE_OG_IMAGE_WIDTH,
  SITE_TITLE,
  SITE_TWITTER,
  SITE_URL,
} from "@constant/constant";

function App() {
  const [visible, setVisible] = useState(false);

  const toggleVisible = () => {
    const scrolled = document.documentElement.scrollTop;
    if (scrolled > 300) {
      setVisible(true);
    } else if (scrolled <= 300) {
      setVisible(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  window.addEventListener("scroll", toggleVisible);

  return (
    <HelmetProvider>
      <Helmet>
        <title>{SITE_TITLE}</title>
        <link rel="canonical" href={SITE_URL} />
        <meta name="description" content={SITE_DESCRIPTION} />
        <meta name="theme-color" content="#000000" />

        {/* Favicon */}
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="icon" type="image/png" sizes="192x192" href="/logo192.png" />
        <link rel="icon" type="image/png" sizes="512x512" href="/logo512.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />

        {/* Open Graph */}
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={SITE_TITLE} />
        <meta property="og:description" content={SITE_DESCRIPTION} />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:image" content={SITE_OG_IMAGE} />
        <meta property="og:image:secure_url" content={SITE_OG_IMAGE} />
        <meta property="og:image:width" content={SITE_OG_IMAGE_WIDTH} />
        <meta property="og:image:height" content={SITE_OG_IMAGE_HEIGHT} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={SITE_TWITTER} />
        <meta name="twitter:creator" content={SITE_TWITTER} />
        <meta name="twitter:title" content={SITE_TITLE} />
        <meta name="twitter:description" content={SITE_DESCRIPTION} />
        <meta name="twitter:image" content={SITE_OG_IMAGE} />
      </Helmet>

      <AlertDialogProvider>
        <AccountContextProvider>
          <ModalContextProvider>
            <ChainSelector>
              <CoinsContextProvider>
                <TxModalProvider>
                  <>
                    <NavigationBar />

                    {/* Route */}
                    <Routes>
                      <Route path="*" element={<NotFoundPage />} />
                      <Route index element={<HomePage />} />
                      <Route path="air-force-lunc" element={<AirForceLunc />} />
                      <Route path="lunc-zombies" element={<LuncZombies />} />
                      <Route
                        path="collection/:query"
                        element={<CollectionPage />}
                      />
                      <Route
                        path="collection/:query/edit"
                        element={<EditCollectionPage />}
                      />
                      <Route path="collections" element={<CollectionsPage />} />
                      <Route
                        path="collection/create"
                        element={<CreateCollectionPage />}
                      />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="profile/edit" element={<EditProfilePage />} />
                      <Route path="item/:id" element={<ItemPage />} />
                      <Route path="item/create" element={<CreateItemPage />} />
                      <Route
                        path="item/create/bulk"
                        element={<BulkCreateItemPage />}
                      />
                      <Route
                        path="item/:id/mint"
                        element={<MintableItemPage />}
                      />
                      <Route path="item/:id/edit" element={<EditItemPage />} />
                      <Route
                        path="nft/:collectionQuery/:tokenId"
                        element={<NftPage />}
                      />
                      <Route path="tokens" element={<TokensPage />} />
                      <Route path="admin" element={<AdminPage />} />
                      <Route path="launchpad" element={<LaunchpadPage />} />
                      <Route path="user/:username" element={<UserPage />} />
                    </Routes>
                    {/* End Route */}

                    {/* Modals */}
                    <AlertDialog />
                    <TxModal />
                    <WalletModal />
                    <RegisterModal />
                    {/* End Modals */}

                    <Footer />
                  </>
                </TxModalProvider>
              </CoinsContextProvider>
            </ChainSelector>
          </ModalContextProvider>
        </AccountContextProvider>
      </AlertDialogProvider>
    </HelmetProvider>
  );
}

export default App;
